import { useSettings } from '@capsicum/common';
import { type NCWSendAssetType, usePartners } from '@capsicum/socios-core';
import { useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';

import { useTokenSupportingPlatformsQuery, v2SupportedDestinationsModalConfirmDisabledAtom } from '../data-access';

import { useV2SupportedDestinationsWarningModalHashes } from './useV2SupportedDestinationsWarningModalHashes';

export type UseSharedV2FTSupportedDestinationsWarningModalProps = {
	asset?: NCWSendAssetType;
	onSubmit: () => void;
	showModal: (props: { exchanges: Array<string>; hash: string; wallets: Array<string> }) => void;
};

export const useV2FTSupportedDestinationsWarningModal = ({
	asset,
	onSubmit: onSubmitProp,
	showModal
}: UseSharedV2FTSupportedDestinationsWarningModalProps) => {
	const {
		areSettingsLoading,
		settings: { v2SendWarningModal }
	} = useSettings();

	const { arePartnersLoading, getPartnerBy } = usePartners();
	const { isHashDifferent } = useV2SupportedDestinationsWarningModalHashes();

	const setV2SupportedDestinationsModalConfirmDisabledAtom = useSetAtom(
		v2SupportedDestinationsModalConfirmDisabledAtom
	);

	const partner = useMemo(
		() => getPartnerBy(partner => partner.currencyId === asset?.assetId),
		[asset, getPartnerBy]
	);

	const isExternalToken = !partner;

	const { data, isLoading: isTokenSupportingPlatformsLoading } = useTokenSupportingPlatformsQuery({
		options: { enabled: !isExternalToken && v2SendWarningModal && !areSettingsLoading },
		params: { 'types[]': ['wallet'] }
	});

	const wallets = useMemo(
		() =>
			(data ?? []).reduce<Array<string>>((destinations, destination) => {
				if (destination.name) destinations.push(destination.name);

				return destinations;
			}, []),
		[data]
	);

	const exchanges = useMemo(
		() =>
			((asset && partner?.tokenSupportingPlatforms) ?? []).reduce<Array<string>>((destinations, destination) => {
				if (destination.name && destination.type === 'exchange') destinations.push(destination.name);

				return destinations;
			}, []),
		[asset, partner]
	);

	const walletsHash = useMemo(() => wallets.sort().join('::'), [wallets]);
	const exchangesHash = useMemo(() => exchanges.sort().join('::'), [exchanges]);
	const hash = useMemo(() => `${walletsHash}||${exchangesHash}`, [walletsHash, exchangesHash]);
	const isV2FT = !isExternalToken && asset?.assetType === 'token' && asset?.decimalDetails === 18;
	const isLoading = arePartnersLoading || areSettingsLoading || isTokenSupportingPlatformsLoading;

	const shouldShowModal = v2SendWarningModal && isV2FT && asset?.assetId && isHashDifferent(asset.assetId, hash);

	return {
		handleSubmit: useCallback(() => {
			if (isLoading) return;

			setV2SupportedDestinationsModalConfirmDisabledAtom(true);

			if (!shouldShowModal) {
				onSubmitProp();

				return;
			}

			showModal({ exchanges, hash, wallets });
		}, [
			hash,
			wallets,
			isLoading,
			showModal,
			exchanges,
			onSubmitProp,
			shouldShowModal,
			setV2SupportedDestinationsModalConfirmDisabledAtom
		]),

		isLoading
	};
};
